import { useTranslation } from "react-i18next"
import { LoadingSpinner } from "@/components/loading-spinner"
import { Banner } from "@/components/ui/banner"
import { Dialog, DialogContentCustom, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Tag } from "@/components/ui/tag"
import { BetterDocBodyViewer } from "@/modules/admin/better-doc/components/better-doc-body-viewer"
import { BetterDocField } from "@/modules/admin/better-doc/components/better-doc-field"
import { BetterDocSessionLink } from "@/modules/admin/better-doc/components/better-doc-session-link"
import { useBetterDocFetch } from "@/modules/admin/better-doc/hooks/useBetterDocFetch"
import { getBetterDocEnumLabel } from "@/modules/admin/better-doc/lib/better-doc-enum-label"
import { EXCHANGE_OUTCOME_TAG_VARIANTS } from "@/modules/admin/better-doc/lib/better-doc-tag-variants"
import {
  formatBetterDocBoolean,
  formatBetterDocDateTime,
  formatBetterDocValue,
} from "@/modules/admin/better-doc/lib/format-better-doc-value"
import {
  BetterDocExchangeDetail,
  fetchBetterDocExchange,
} from "@/modules/admin/better-doc/services/betterDocExchangeQuery"

type BetterDocExchangeDialogProps = {
  exchangeId: number
  onClose: () => void
}

type BetterDocExchangeDetailContentProps = {
  detail: BetterDocExchangeDetail
  onClose: () => void
}

const BetterDocExchangeDetailContent = ({ detail, onClose }: BetterDocExchangeDetailContentProps) => {
  const { t } = useTranslation()
  const { exchange, sessionIds } = detail

  return (
    <div className="flex flex-col gap-6" data-testid="better-doc-admin-exchange-detail">
      <div className="grid grid-cols-3 gap-4">
        <BetterDocField
          label={t("admin.betterDoc.exchangeDetail.operation")}
          value={getBetterDocEnumLabel(t, "exchangeOperation", exchange.operation)}
          testId="better-doc-admin-exchange-detail-operation"
        />
        <BetterDocField
          label={t("admin.betterDoc.exchangeDetail.actor")}
          value={exchange.actor}
          testId="better-doc-admin-exchange-detail-actor"
        />
        <BetterDocField
          label={t("admin.betterDoc.exchangeDetail.outcome")}
          value={
            <Tag
              type={EXCHANGE_OUTCOME_TAG_VARIANTS[exchange.outcome]}
              label={getBetterDocEnumLabel(t, "exchangeOutcome", exchange.outcome)}
            />
          }
          testId="better-doc-admin-exchange-detail-outcome"
        />
        <BetterDocField
          label={t("admin.betterDoc.exchangeDetail.failureKind")}
          value={formatBetterDocValue(exchange.failureKind)}
          testId="better-doc-admin-exchange-detail-failure-kind"
        />
        <BetterDocField
          label={t("admin.betterDoc.exchangeDetail.hasUnexpectedResponse")}
          value={formatBetterDocBoolean(t, exchange.hasUnexpectedResponse)}
          testId="better-doc-admin-exchange-detail-unexpected-response"
        />
        <BetterDocField
          label={t("admin.betterDoc.exchangeDetail.request")}
          value={`${exchange.httpMethod} ${exchange.requestUri}`}
          testId="better-doc-admin-exchange-detail-request"
          copyText={exchange.requestUri}
        />
        <BetterDocField
          label={t("admin.betterDoc.exchangeDetail.responseStatusCode")}
          value={formatBetterDocValue(exchange.responseStatusCode)}
          testId="better-doc-admin-exchange-detail-status-code"
        />
        <BetterDocField
          label={t("admin.betterDoc.exchangeDetail.durationMilliseconds")}
          value={formatBetterDocValue(exchange.durationMilliseconds)}
          testId="better-doc-admin-exchange-detail-duration"
        />
        <BetterDocField
          label={t("admin.betterDoc.exchangeDetail.startedAt")}
          value={formatBetterDocDateTime(exchange.startedAt)}
          testId="better-doc-admin-exchange-detail-started-at"
        />
        <BetterDocField
          label={t("admin.betterDoc.exchangeDetail.completedAt")}
          value={formatBetterDocDateTime(exchange.completedAt)}
          testId="better-doc-admin-exchange-detail-completed-at"
        />
        <BetterDocField
          label={t("admin.betterDoc.exchangeDetail.isTestCase")}
          value={formatBetterDocBoolean(t, exchange.isTestCase)}
          testId="better-doc-admin-exchange-detail-test-case"
        />
        <BetterDocField
          label={t("admin.betterDoc.exchangeDetail.progressNoteId")}
          value={formatBetterDocValue(exchange.progressNoteId)}
          testId="better-doc-admin-exchange-detail-progress-note"
        />
        <BetterDocField
          label={t("admin.betterDoc.exchangeDetail.patientId")}
          value={formatBetterDocValue(exchange.patientId)}
          testId="better-doc-admin-exchange-detail-patient"
        />
        <BetterDocField
          label={t("admin.betterDoc.exchangeDetail.traceParent")}
          value={formatBetterDocValue(detail.traceParent)}
          testId="better-doc-admin-exchange-detail-trace-parent"
        />
        <BetterDocField
          label={t("admin.betterDoc.exchangeDetail.requestContentType")}
          value={formatBetterDocValue(detail.requestContentType)}
          testId="better-doc-admin-exchange-detail-request-content-type"
        />
        <BetterDocField
          label={t("admin.betterDoc.exchangeDetail.responseContentType")}
          value={formatBetterDocValue(detail.responseContentType)}
          testId="better-doc-admin-exchange-detail-response-content-type"
        />
      </div>
      <div className="flex flex-wrap items-center gap-2" data-testid="better-doc-admin-exchange-detail-sessions">
        <span className="text-on-surface-subdued text-xs">{t("admin.betterDoc.exchangeDetail.sessions")}</span>
        {sessionIds.length === 0 ? (
          <span className="text-on-surface text-sm">{t("admin.betterDoc.exchangeDetail.noSessions")}</span>
        ) : (
          sessionIds.map((sessionId) => (
            <BetterDocSessionLink key={sessionId} sessionId={sessionId} onNavigate={onClose} />
          ))
        )}
      </div>
      <BetterDocBodyViewer
        title={t("admin.betterDoc.exchangeDetail.requestHeaders")}
        body={detail.requestHeaders}
        testId="better-doc-admin-exchange-request-headers"
      />
      <BetterDocBodyViewer
        title={t("admin.betterDoc.exchangeDetail.requestBody")}
        body={detail.requestBody}
        testId="better-doc-admin-exchange-request-body"
      />
      <BetterDocBodyViewer
        title={t("admin.betterDoc.exchangeDetail.responseHeaders")}
        body={detail.responseHeaders}
        testId="better-doc-admin-exchange-response-headers"
      />
      <BetterDocBodyViewer
        title={t("admin.betterDoc.exchangeDetail.responseBody")}
        body={detail.responseBody}
        testId="better-doc-admin-exchange-response-body"
      />
    </div>
  )
}

export const BetterDocExchangeDialog = ({ exchangeId, onClose }: BetterDocExchangeDialogProps) => {
  const { t } = useTranslation()
  const exchangeState = useBetterDocFetch(exchangeId, fetchBetterDocExchange)

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onClose()
    }
  }

  const renderContent = () => {
    switch (exchangeState.status) {
      case "idle":
      case "loading":
        return <LoadingSpinner size={24} className="flex justify-center p-8" />
      case "failed":
        return (
          <Banner type="inline" variant="critical" data-testid="better-doc-admin-exchange-dialog-error">
            {t("admin.betterDoc.requestFailed")}
          </Banner>
        )
      case "loaded":
        return exchangeState.data === null ? (
          <Banner type="inline" variant="info" data-testid="better-doc-admin-exchange-dialog-not-found">
            {t("admin.betterDoc.exchangeDialog.notFound")}
          </Banner>
        ) : (
          <BetterDocExchangeDetailContent detail={exchangeState.data} onClose={onClose} />
        )
    }
  }

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContentCustom className="max-h-[90vh] overflow-y-auto" data-testid="better-doc-admin-exchange-dialog">
        <DialogHeader>
          <DialogTitle>{t("admin.betterDoc.exchangeDialog.title", { exchangeId })}</DialogTitle>
        </DialogHeader>
        <div className="px-6 pb-6">{renderContent()}</div>
      </DialogContentCustom>
    </Dialog>
  )
}
